import {
  getLocalTimeZone,
  today,
  parseAbsolute,
} from "@internationalized/date";
import { LocalStorage } from "@jetblue/dotcom-storage";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SmartBannerDismissalData } from ".";

const STORAGE_KEY = "jb-smart-banner-dismissed";
const DEFAULT_DISMISSAL_DURATION = 90;

const isAndroidDevice = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes("android");
};

const getDaysSinceDismissal = (): number | null => {
  try {
    const dismissedData = LocalStorage.get(STORAGE_KEY);
    if (!dismissedData) {
      return null;
    }

    const parsed = JSON.parse(dismissedData) as SmartBannerDismissalData;
    const dismissalDate = parseAbsolute(
      parsed.dismissedAt,
      getLocalTimeZone(),
    ).toDate();
    const now = today(getLocalTimeZone()).toDate(getLocalTimeZone());

    const daysSinceDismissal =
      (now.getTime() - dismissalDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceDismissal;
  } catch (error) {
    console.warn("Error reading smart banner dismissal data:", error);
    return null;
  }
};

const isBannerDismissed = (dismissalDurationDays: number): boolean => {
  const daysSinceDismissal = getDaysSinceDismissal();
  if (daysSinceDismissal === null) {
    return false;
  }
  return daysSinceDismissal < dismissalDurationDays;
};

const getDaysUntilBannerShows = (dismissalDurationDays: number): number => {
  const daysSinceDismissal = getDaysSinceDismissal();
  if (daysSinceDismissal === null) {
    return 0;
  }
  const remainingDays = dismissalDurationDays - daysSinceDismissal;
  return Math.max(0, Math.ceil(remainingDays));
};

export type UseSmartBannerOptions = {
  /**
   * The app store URL where users can download the app
   */
  appStoreUrl: string;

  /**
   * Optional deep link URL to open the app directly if installed
   */
  appDeepLink?: string;

  /**
   * Number of days to wait before showing the banner again after dismissal
   * @default 90
   */
  dismissalDurationDays?: number;

  /**
   * Whether to show the banner automatically
   * @default true
   */
  isEnabled?: boolean;
};
export type UseSmartBannerReturn = {
  isVisible: boolean;
  isAndroid: boolean;
  dismissBanner: () => void;
  openApp: () => void;
  daysUntilNextShow: number;
};
export const useSmartBanner = ({
  appDeepLink,
  appStoreUrl,
  dismissalDurationDays = DEFAULT_DISMISSAL_DURATION,
  isEnabled = true,
}: UseSmartBannerOptions) => {
  const timeoutReference = useRef<NodeJS.Timeout | null>(null);

  const androidDevice = isAndroidDevice();
  const isDismissed = isBannerDismissed(dismissalDurationDays);

  const [isAndroid] = useState(androidDevice);
  const [isVisible, setIsVisible] = useState(
    () => isEnabled && androidDevice && !isDismissed,
  );
  const [daysUntilNextShow, setDaysUntilNextShow] = useState(() =>
    isDismissed ? getDaysUntilBannerShows(dismissalDurationDays) : 0,
  );

  useEffect(() => {
    if (!isEnabled) {
      setIsVisible(false);
    }
  }, [isEnabled]);

  const dismissBanner = useCallback(() => {
    const dismissalData: SmartBannerDismissalData = {
      dismissedAt: new Date().toISOString(),
    };

    LocalStorage.set(STORAGE_KEY, JSON.stringify(dismissalData));
    setIsVisible(false);
    setDaysUntilNextShow(dismissalDurationDays);
  }, [dismissalDurationDays]);

  const openApp = useCallback(() => {
    if (timeoutReference.current) {
      clearTimeout(timeoutReference.current);
    }

    if (appDeepLink && isAndroid) {
      globalThis.location.assign(appDeepLink);

      timeoutReference.current = setTimeout(() => {
        if (document.visibilityState === "visible") {
          globalThis.location.assign(appStoreUrl);
        }
      }, 2000);
    } else {
      globalThis.location.assign(appStoreUrl);
    }
  }, [appDeepLink, appStoreUrl, isAndroid]);

  useEffect(
    () => () => {
      if (timeoutReference.current) {
        clearTimeout(timeoutReference.current);
      }
    },
    [],
  );

  return {
    daysUntilNextShow,
    dismissBanner,
    isAndroid,
    isVisible,
    openApp,
  };
};
